import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Eye, EyeOff, School, Mail, Phone, User, Lock, MessageSquare, Timer } from 'lucide-react'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'

const schools = [
  '大悟一中',
  '楚才高中',
  '城关中学',
  '行健高中',
  '思源学校',
  '天有学校',
  '实验中学',
  '其他'
]

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, register, isLoading, isAuthenticated } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  // 支持 ?tab=register 直达注册表单
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'register' ? 'register' : 'login'
  )

  // 已登录用户访问登录页时直接回首页（避免"已登录却还要登录"的困惑）
  const redirectedRef = useRef(false)
  useEffect(() => {
    if (isAuthenticated && !redirectedRef.current) {
      redirectedRef.current = true
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  // 邮箱验证码相关状态
  const [emailCodeSent, setEmailCodeSent] = useState(false)
  const [emailCodeCountdown, setEmailCodeCountdown] = useState(0)
  const [sendingEmailCode, setSendingEmailCode] = useState(false)

  // 登录表单状态
  const [loginForm, setLoginForm] = useState({
    account: '',
    password: '',
    remember: false
  })

  // 注册表单状态
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    emailCode: '',
    qq: '',
    password: '',
    confirmPassword: '',
    school: '',
    nickname: '',
    agreement: false
  })

  // 邮箱验证码倒计时
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (emailCodeCountdown > 0) {
      timer = setTimeout(() => {
        setEmailCodeCountdown(emailCodeCountdown - 1)
      }, 1000)
    }
    return () => clearTimeout(timer)
  }, [emailCodeCountdown])

  // 发送邮箱验证码
  const sendEmailCode = async () => {
    if (!registerForm.email.trim()) {
      toast.error('请先输入邮箱地址')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(registerForm.email)) {
      toast.error('请输入有效的邮箱地址')
      return
    }

    setSendingEmailCode(true)
    try {
      // 后端暂无邮件服务：注册无需邮箱验证码，直接提示用户即可
      await new Promise(resolve => setTimeout(resolve, 500))
      toast.info('当前版本注册无需邮箱验证码，请直接填写密码完成注册')
      setEmailCodeSent(true)
      setEmailCodeCountdown(0)
    } catch (error) {
      toast.error('发送验证码失败，请稍后重试')
    } finally {
      setSendingEmailCode(false)
    }
  }

  // 验证QQ号格式
  const isValidQQ = (qq: string) => {
    return /^[1-9][0-9]{4,10}$/.test(qq)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (!loginForm.account.trim()) {
        toast.error('请输入账号')
        return
      }
      
      if (!loginForm.password.trim()) {
        toast.error('请输入密码')
        return
      }

      if (loginForm.password.length < 6) {
        toast.error('密码长度至少6位')
        return
      }

      await login({
        account: loginForm.account.trim(),
        password: loginForm.password,
        remember: loginForm.remember
      })
      
      toast.success('登录成功！欢迎回到大悟万能墙')
      navigate('/')
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('登录失败，请稍后重试')
      }
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      // 详细表单验证
      if (!registerForm.username.trim()) {
        toast.error('请输入用户名')
        return
      }

      if (registerForm.username.length < 2) {
        toast.error('用户名至少2个字符')
        return
      }

      if (!registerForm.email.trim()) {
        toast.error('请输入邮箱地址')
        return
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(registerForm.email)) {
        toast.error('请输入有效的邮箱地址')
        return
      }

      if (registerForm.qq && !isValidQQ(registerForm.qq)) {
        toast.error('请输入有效的QQ号')
        return
      }

      if (!registerForm.password.trim()) {
        toast.error('请输入密码')
        return
      }

      if (registerForm.password.length < 6) {
        toast.error('密码长度至少6位')
        return
      }

      if (registerForm.password !== registerForm.confirmPassword) {
        toast.error('两次输入的密码不一致')
        return
      }

      if (!registerForm.agreement) {
        toast.error('请同意用户协议和隐私政策')
        return
      }

      await register({
        username: registerForm.username.trim(),
        email: registerForm.email.trim(),
        emailCode: registerForm.emailCode.trim(),
        qq: registerForm.qq.trim() || undefined,
        password: registerForm.password,
        school: registerForm.school,
        nickname: registerForm.nickname.trim() || undefined
      })
      
      toast.success('注册成功！欢迎加入大悟万能墙')
      navigate('/')
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('注册失败，请稍后重试')
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* 背景装饰漂浮色块 */}
      <div className="deco-blob w-72 h-72 bg-blue-400 -top-20 -left-20" />
      <div className="deco-blob w-80 h-80 bg-purple-400 bottom-0 right-0" style={{ animationDelay: '-4s' }} />
      <div className="deco-blob w-56 h-56 bg-pink-400 top-1/3 right-1/4" style={{ animationDelay: '-8s' }} />
      
      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">悟</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">大悟万能墙</h1>
              <p className="text-sm text-gray-500">校园社交，从这里开始</p>
            </div>
          </div>
        </div>

        <Card className="shadow-lg border-0">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl">欢迎使用</CardTitle>
            <CardDescription>连接校园，分享生活</CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">登录</TabsTrigger>
                <TabsTrigger value="register">注册</TabsTrigger>
              </TabsList>
              
              {/* 登录表单 */}
              <TabsContent value="login">
                
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="account">账号</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="account"
                        type="text"
                        placeholder="用户名/邮箱/QQ号"
                        className="pl-10"
                        value={loginForm.account}
                        onChange={(e) => setLoginForm({...loginForm, account: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">密码</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="请输入密码 "
                        className="pl-10 pr-10"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember"
                        checked={loginForm.remember}
                        onCheckedChange={(checked) => setLoginForm({...loginForm, remember: checked as boolean})}
                      />
                      <Label htmlFor="remember" className="text-sm text-gray-600">记住我</Label>
                    </div>
                    <span className="text-sm text-gray-400">忘记密码请联系管理员</span>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                    disabled={isLoading}
                  >
                    {isLoading ? '登录中...' : '登录'}
                  </Button>
                </form>
              </TabsContent>
              
              {/* 注册表单 */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">用户名</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="username"
                        type="text"
                        placeholder="请输入用户名"
                        className="pl-10"
                        value={registerForm.username}
                        onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">邮箱地址 *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="请输入邮箱地址"
                        className="pl-10"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500">如果是QQ邮箱，注册后将自动获取QQ头像</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emailCode">邮箱验证码 *</Label>
                    <div className="flex space-x-2">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          id="emailCode"
                          type="text"
                          placeholder="请输入验证码（选填，当前无需验证码）"
                          className="pl-10"
                          value={registerForm.emailCode}
                          onChange={(e) => setRegisterForm({...registerForm, emailCode: e.target.value})}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="whitespace-nowrap"
                        onClick={sendEmailCode}
                        disabled={sendingEmailCode || emailCodeCountdown > 0}
                      >
                        {sendingEmailCode ? (
                          '发送中...'
                        ) : emailCodeCountdown > 0 ? (
                          <div className="flex items-center space-x-1">
                            <Timer className="w-3 h-3" />
                            <span>{emailCodeCountdown}s</span>
                          </div>
                        ) : (
                          '发送验证码'
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="qq">QQ号</Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="qq"
                        type="text"
                        placeholder="请输入QQ号"
                        className="pl-10"
                        value={registerForm.qq}
                        onChange={(e) => setRegisterForm({...registerForm, qq: e.target.value})}
                      />
                    </div>
                    <p className="text-xs text-gray-500">填写QQ号后将自动获取QQ头像和昵称</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nickname">昵称</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="nickname"
                        type="text"
                        placeholder="请输入昵称"
                        className="pl-10"
                        value={registerForm.nickname}
                        onChange={(e) => setRegisterForm({...registerForm, nickname: e.target.value})}
                      />
                    </div>
                    <p className="text-xs text-gray-500">如果填写了QQ号，将自动获取QQ昵称</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="school">所在学校</Label>
                    <div className="relative">
                      <School className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                      <Select value={registerForm.school} onValueChange={(value) => setRegisterForm({...registerForm, school: value})}>
                        <SelectTrigger className="pl-10">
                          <SelectValue placeholder="请选择学校" />
                        </SelectTrigger>
                        <SelectContent>
                          {schools.map((school) => (
                            <SelectItem key={school} value={school}>{school}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-password">密码</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="reg-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="请输入密码"
                        className="pl-10 pr-10"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">确认密码</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="请再次输入密码"
                        className="pl-10 pr-10"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="agreement"
                      checked={registerForm.agreement}
                      onCheckedChange={(checked) => setRegisterForm({...registerForm, agreement: checked as boolean})}
                    />
                    <Label htmlFor="agreement" className="text-sm text-gray-600">
                      我已阅读并同意
                      <Link to="/terms" className="text-orange-500 hover:text-orange-600 mx-1">用户协议</Link>
                      和
                      <Link to="/privacy" className="text-orange-500 hover:text-orange-600 mx-1">隐私政策</Link>
                    </Label>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                    disabled={isLoading}
                  >
                    {isLoading ? '注册中...' : '注册'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* 底部链接 */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <div className="flex justify-center space-x-4 mt-2">
            <Link to="/terms" className="text-orange-500 hover:text-orange-600 transition-colors">用户协议</Link>
            <Link to="/privacy" className="text-orange-500 hover:text-orange-600 transition-colors">隐私政策</Link>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-400">
              © 2025 大悟万能墙 · 让校园生活更精彩
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}